/**
 * Canopy Units — length-unit conversion at the resolver's read boundary.
 *
 * Canopy separates the **declared space** (what the author typed: the
 * declaration's `value`/`min`/`max`/`step`, state values, `setValue` arguments,
 * transition endpoints) from the **canonical space** (metres — what the scene
 * consumes). `getValue()` is the single conversion point; everything else in the
 * resolver stays in declared space.
 *
 * Only `number` and `vec3` declarations carry `units`; every other type ignores
 * it. Unknown unit strings are treated as canonical and warned once.
 *
 * See docs/canopy/CANOPY_UNITS.md for the normative spec.
 */

import type { PropertyDeclaration, Units } from '../../scenedata/behaviors/propertiesdata';
import type { PropertyValue } from '../../behaviors/tracebehavior';
import { Logger } from '../../utils/Logger';

/**
 * Multiply a declared-space value by this factor to get canonical metres.
 * `0.3048` is the exact international foot — NOT the legacy `1 / 3.28084`,
 * so authored values round-trip to the same float across engines.
 */
export const UNIT_TO_CANONICAL: Record<Units, number> = {
    Meters: 1,
    Feet: 0.3048,
};

/** Short display suffix per unit, for value readouts and labels. */
export const UNIT_SUFFIX: Record<Units, string> = {
    Meters: 'm',
    Feet: 'ft',
};

/** Units strings already warned about — one warning per unknown string, process-wide. */
const _warnedUnknownUnits = new Set<string>();

/**
 * Conversion factor for a units tag. Absent units, `'Meters'`, and any
 * unrecognized string all resolve to 1 (the unrecognized case warns once).
 */
export function unitFactor(units: Units | undefined): number {
    if (units === undefined) return 1;
    const factor = UNIT_TO_CANONICAL[units];
    if (typeof factor !== 'number') {
        if (!_warnedUnknownUnits.has(units)) {
            _warnedUnknownUnits.add(units);
            Logger.warn(`[Canopy] Unknown units "${units}" — treating the value as canonical metres.`);
        }
        return 1;
    }
    return factor;
}

/**
 * The units tag that governs a declaration's values. Only `number` and `vec3`
 * carry length units; every other type ignores a stray `units` field, and an
 * absent declaration is canonical. CANOPY_UNITS §2.
 */
export function declarationUnits(decl: PropertyDeclaration | undefined): Units | undefined {
    if (!decl) return undefined;
    return decl.type === 'number' || decl.type === 'vec3' ? decl.units : undefined;
}

/** Display suffix for a units tag (`undefined` when there is nothing to show). */
export function unitSuffix(units: Units | undefined): string | undefined {
    if (units === undefined) return undefined;
    return UNIT_SUFFIX[units];
}

function _map(value: PropertyValue | undefined, fn: (n: number) => number): PropertyValue | undefined {
    if (typeof value === 'number') return fn(value);
    if (Array.isArray(value) && value.length === 3) {
        return [fn(value[0]), fn(value[1]), fn(value[2])];
    }
    // Strings, booleans and vec4 carry no length meaning — pass through untouched.
    return value;
}

/** Declared space → canonical metres. Non-length values pass through unchanged. */
export function toCanonical(units: Units | undefined, value: PropertyValue | undefined): PropertyValue | undefined {
    const factor = unitFactor(units);
    return factor === 1 ? value : _map(value, n => n * factor);
}

/**
 * Canonical metres → declared space. Non-length values pass through unchanged.
 *
 * Divides by the factor rather than multiplying by its reciprocal: an
 * import-then-read round trip (`toCanonical(u, fromCanonical(u, x))`) then
 * returns `x` exactly for most values and within one ulp for the rest
 * (`1 → 0.9999999999999999`), where the reciprocal form drifts more often.
 */
export function fromCanonical(units: Units | undefined, value: PropertyValue | undefined): PropertyValue | undefined {
    const factor = unitFactor(units);
    return factor === 1 ? value : _map(value, n => n / factor);
}

/** @internal — test seam: forget which unknown unit strings have been warned about. */
export function _resetUnitWarnings(): void {
    _warnedUnknownUnits.clear();
}
